import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  CircularProgress,
  ListItem,
  Dialog,
  Button,
  Card,
  Box,
  Typography,
  TextField,
  IconButton,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import PersonIcon from '@material-ui/icons/Person';
import ReceiptIcon from '@material-ui/icons/Receipt';
import TodayIcon from '@material-ui/icons/Today';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { makeStyles } from '@material-ui/core/styles';

// Ícones para o novo botão
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import PersonAddIcon from '@material-ui/icons/PersonAdd';

import UIFormTitle from '../../../UI/Form/Title/UIFormTitle';
import UIContainer from '../../../UI/Container/UIContainer';
import useApi from '../../../../utils/useApi';
import SessionCard from '../Card/sessionCard';
import { emptyObject, normalizeText } from '../../../../utils/utils';
import SaleNew from '../NewSale/saleNew';
import StoreContext from '../../../Store/context';
import ClientCreateModal from '../../../Client/ClientCreateModal';
import { isSessionUpdatedToday } from '../sessionUtils';

const initialSaleSession = {
  onlyCalculate: true,
  identifier: 'Comanda sem nome',
  customer: 1,
  promotion: 1,
  products: {},
  services: {},
  interest: {},
  splitRemaining: 0,
};

// Estilização exclusiva do botão de nova comanda
const useStyles = makeStyles((theme) => ({
  createCard: {
    height: '100%',
    // Tenta manter a altura similar aos SessionCards existentes
    minHeight: '150px',
    display: 'flex',
    flexDirection: 'column',
    border: `2px dashed ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    borderRadius: 8,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.dark,
      backgroundColor: theme.palette.action.hover,
    }
  },
  quickActionArea: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  inputArea: {
    padding: theme.spacing(0.75, 1),
    borderTop: '1px dashed rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff', // Destaque sutil para o input
  },
  autocompleteRoot: {
    width: '100%',
    '& .MuiInput-underline:before': { borderBottom: 'none' },
    '& .MuiInput-underline:after': { borderBottom: 'none' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
  },
  inputField: {
    '& .MuiInput-underline:before': { borderBottom: 'none' },
    '& .MuiInput-underline:after': { borderBottom: 'none' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
  }
}));

const NewSaleSession = (children) => {
  const { collapseNew, changeCollapseNew } = children;
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [modalSale, setModalSale] = useState(false);
  const [modalValues, setModalValues] = useState(false);
  const [cardValues, setCardValues] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('today'); // 'all' | 'today'

  // Novo state para o input manual do card
  const [customName, setCustomName] = useState('');
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [openClientModal, setOpenClientModal] = useState(false);

  const { credencials } = useContext(StoreContext);

  const [salesSessionsLoad, salesSessionsInfo] = useApi({
    debounceDelay: 500,
    method: 'get',
    url: '/sales/sessions',
  });

  const [customersLoad, customersInfo] = useApi({
    method: 'get',
    url: '/customers',
  });

  const [newSessionLoad, newSessionInfo] = useApi({
    debounceDelay: 500,
    method: 'post',
    url: '/sales/sessions',
  });

  function handleModalSale(values) {
    if (values) {
      const data = {
        saleMetadata: values,
      };
      setModalValues(data);
    }
    if (modalValues) setModalValues(null);
    if (modalSale) refreshCards();
    setModalSale(!modalSale);
  }

  async function refreshCards() {
    setCardValues(null);
    await salesSessionsLoad({
      onCompleted: (res) => {
        if (res) {
          const { data } = res;
          const newCardValues = data.map((value) => {
            const existsCard =
              cardValues && cardValues.find((val) => val.id === value.id);
            if (existsCard) {
              const response = { ...value, oldValue: existsCard.oldValue };
              return response;
            }
            const response = { ...value, oldValue: 0 };

            return response;
          });
          setCardValues(newCardValues);
        } else {
          setCardValues(null);
        }
      },
    });
  }

  function onSubmitNewSession(sessionName, customerObj = null) {
    // Garante um nome padrão se vier vazio
    const nameToUse = sessionName || (customerObj ? `${customerObj.name} ${customerObj.lastName || ''}`.trim() : 'Nova Comanda');

    const data = emptyObject({ ...initialSaleSession });
    data.responsible = `${credencials.name} ${credencials.lastName}`;
    data.identifier = nameToUse;
    if (customerObj && customerObj.id) {
      data.customer = Number(customerObj.id);
    }

    newSessionLoad({
      data,
      onCompleted: (res) => {
        setCustomName(''); // Limpa o input após criar
        setAutocompleteKey((k) => k + 1);
        refreshCards();
        customersLoad();
        if (res && res.data && res.data.session) {
          try {
            const parsedSession = typeof res.data.session === 'string'
              ? JSON.parse(res.data.session)
              : res.data.session;
            parsedSession.id = res.data.id || parsedSession.session_id;
            parsedSession.session_id = res.data.id || parsedSession.session_id;
            handleModalSale(parsedSession);
          } catch (e) {
            console.error('Erro ao abrir comanda criada:', e);
          }
        }
      },
      onError: (err) => {
        console.error('Erro ao criar comanda:', err);
      },
    });
  }

  function setOldValues(id, currentValue) {
    const newOldValues = cardValues.find((value) => value.id === id);
    newOldValues.oldValue = currentValue;
    setCardValues(cardValues);
  }

  useEffect(() => {
    refreshCards();
    customersLoad();
  }, [collapseNew]);

  const totalCount = (cardValues || []).length;
  const todayCount = (cardValues || []).filter(isSessionUpdatedToday).length;

  const filteredCards = (cardValues || []).filter((item) => {
    // Filtro rápido por período/status
    if (filterMode === 'today' && !isSessionUpdatedToday(item)) return false;

    const cleanSearch = searchTerm.trim();
    if (!cleanSearch) return true;

    const term = normalizeText(cleanSearch);
    const idMatch = String(item.id || '').includes(term);
    const sessionMatch = String(item.session_id || '').includes(term);
    const identifierMatch = normalizeText(item.identifier).includes(term);
    const responsibleMatch = normalizeText(item.responsible).includes(term);
    const obsMatch = normalizeText(item.observation).includes(term);

    // Cliente vinculado
    const customer = item.customerData || item.customer || item.sale_metadata?.customer;
    let custMatch = false;
    if (customer) {
      if (typeof customer === 'object') {
        const custFullName = normalizeText(`${customer.name || ''} ${customer.lastName || ''}`);
        const custCpf = String(customer.cpf || '').replace(/\D/g, '');
        const custPhone = String(customer.phone || customer.whatsapp || '').replace(/\D/g, '');
        custMatch = custFullName.includes(term) || custCpf.includes(term) || custPhone.includes(term);
      } else {
        custMatch = String(customer).includes(term);
      }
    }

    // Itens e produtos dentro da comanda
    let itemsMatch = false;
    const metadata = item.sale_metadata;
    if (metadata) {
      if (Array.isArray(metadata.products)) {
        itemsMatch = itemsMatch || metadata.products.some((p) => normalizeText(p.name).includes(term));
      }
      if (Array.isArray(metadata.services)) {
        itemsMatch = itemsMatch || metadata.services.some((s) => normalizeText(s.name).includes(term));
      }
    }

    return idMatch || sessionMatch || identifierMatch || responsibleMatch || obsMatch || custMatch || itemsMatch;
  });

  return (
    <>
      <UIContainer container direction="row">
        <ListItem
          button
          onClick={changeCollapseNew}
          style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}
        >
          <Button>Recarregar</Button>
          <UIFormTitle title="Comandas" />
        </ListItem>

        {/* Barra de Busca de Comandas */}
        <div style={{ width: '100%', padding: '0 8px 12px 8px' }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Buscar comanda por número, mesa, cliente, item ou atendente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#8c9ba5' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon style={{ fontSize: 18, color: '#8c9ba5' }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </div>

        {/* Filtros Rápidos de Comandas */}
        <div
          style={{
            width: '100%',
            padding: '0 8px 12px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <Chip
            icon={<ListAltIcon style={{ fontSize: 16 }} />}
            label={`Todas (${totalCount})`}
            color={filterMode === 'all' ? 'primary' : 'default'}
            variant={filterMode === 'all' ? 'default' : 'outlined'}
            onClick={() => setFilterMode('all')}
            clickable
            size="small"
            style={{ fontWeight: 600 }}
          />
          <Chip
            icon={<TodayIcon style={{ fontSize: 16 }} />}
            label={`Hoje (${todayCount})`}
            onClick={() => setFilterMode(filterMode === 'today' ? 'all' : 'today')}
            clickable
            size="small"
            style={{
              fontWeight: 600,
              backgroundColor: filterMode === 'today' ? '#059669' : undefined,
              color: filterMode === 'today' ? '#fff' : undefined,
              borderColor: filterMode === 'today' ? '#059669' : undefined,
            }}
            variant={filterMode === 'today' ? 'default' : 'outlined'}
          />
        </div>

        <Grid
          container
          spacing={2}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            padding: '8px',
            gap: '8px'
          }}
        >

          {/* Botão de Nova Comanda (Com Autocomplete Inteligente) */}
          <Grid
            item
            style={{
              flex: '0 0 auto',
              width: mobile ? '100%' : '260px',
              minWidth: mobile ? '100%' : '260px',
              maxWidth: '100%'
            }}
          >
            <Card className={classes.createCard} style={{ height: '180px' }}>
              {/* Área de Clique Rápido */}
              <Box
                className={classes.quickActionArea}
                onClick={() => !newSessionInfo.loading && onSubmitNewSession('Nova Comanda')}
              >
                {newSessionInfo.loading ? (
                  <CircularProgress />
                ) : (
                  <>
                    <AddCircleOutlineIcon style={{ fontSize: 40, color: '#3f51b5', marginBottom: 5 }} />
                    <Typography variant="h6" color="primary" style={{ fontWeight: 'bold' }}>
                      Nova Comanda
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Clique para abrir avulsa
                    </Typography>
                  </>
                )}
              </Box>

              {/* Área de Input com Autocomplete de Clientes ou Avulso */}
              <div className={classes.inputArea}>
                <Autocomplete
                  key={autocompleteKey}
                  freeSolo
                  className={classes.autocompleteRoot}
                  options={customersInfo.data || []}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.label) return option.label;
                    return `${option.name || ''} ${option.lastName || ''}`.trim();
                  }}
                  filterOptions={(options, state) => {
                    const term = normalizeText(state.inputValue);
                    const filtered = !term
                      ? options.slice(0, 10)
                      : options.filter((c) => {
                        const fullName = normalizeText(`${c.name || ''} ${c.lastName || ''}`);
                        const cpf = normalizeText(c.cpf);
                        const phone = normalizeText(c.phone || c.whatsapp);
                        return fullName.includes(term) || cpf.includes(term) || phone.includes(term);
                      }).slice(0, 10);

                    if (term) {
                      filtered.unshift({
                        isActionAvulso: true,
                        name: state.inputValue.trim(),
                        label: `Abrir avulso: "${state.inputValue.trim()}"`,
                      });
                      const exactMatch = options.some(
                        (c) => normalizeText(`${c.name || ''} ${c.lastName || ''}`).trim() === term
                      );
                      if (!exactMatch) {
                        filtered.push({
                          isActionCreate: true,
                          name: state.inputValue.trim(),
                          label: `+ Cadastrar cliente: "${state.inputValue.trim()}"`,
                        });
                      }
                    }
                    return filtered;
                  }}
                  renderOption={(option) => {
                    if (option.isActionAvulso) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', color: '#17497F', fontWeight: 600, fontSize: '0.82rem' }}>
                          <ReceiptIcon style={{ fontSize: 17, color: '#17497F' }} />
                          <span>{option.label}</span>
                        </div>
                      );
                    }
                    if (option.isActionCreate) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', color: '#10b981', fontWeight: 600, fontSize: '0.82rem' }}>
                          <PersonAddIcon style={{ fontSize: 17, color: '#10b981' }} />
                          <span>{option.label}</span>
                        </div>
                      );
                    }
                    const fullName = `${option.name || ''} ${option.lastName || ''}`.trim();
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <PersonIcon style={{ fontSize: 15, color: '#64748b' }} />
                          <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>
                            {fullName || option.name}
                          </span>
                        </div>
                        {(option.cpf || option.whatsapp || option.phone) && (
                          <span style={{ fontSize: '0.7rem', color: '#64748b', paddingLeft: 21 }}>
                            {[option.cpf ? `CPF: ${option.cpf}` : null, option.whatsapp || option.phone].filter(Boolean).join(' • ')}
                          </span>
                        )}
                      </div>
                    );
                  }}
                  onChange={(event, value) => {
                    if (!value) return;
                    if (typeof value === 'string') {
                      onSubmitNewSession(value.trim());
                      return;
                    }
                    if (value.isActionAvulso) {
                      onSubmitNewSession(value.name);
                      return;
                    }
                    if (value.isActionCreate) {
                      setCustomName(value.name);
                      setOpenClientModal(true);
                      setAutocompleteKey((k) => k + 1);
                      return;
                    }
                    // Cliente existente selecionado
                    onSubmitNewSession(null, value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Cliente ou nome..."
                      className={classes.inputField}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: '0.82rem' },
                        endAdornment: (
                          <>
                            <Tooltip title="Cadastrar Novo Cliente">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCustomName(params.inputProps.value || '');
                                  setOpenClientModal(true);
                                }}
                                style={{ padding: 4, color: '#17497F' }}
                              >
                                <PersonAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            </Card>
          </Grid>

          {salesSessionsInfo.loading && (
            <Grid item style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </Grid>
          )}
          {filteredCards &&
            filteredCards.map((values) => (
              <Grid
                item
                key={values.session_id || values.id}
                style={{
                  flex: '0 0 auto',
                  width: mobile ? '100%' : '240px',
                  minWidth: mobile ? '100%' : '240px',
                  maxWidth: '100%'
                }}
              >
                <SessionCard
                  key={values.session_id || values.id}
                  setOldValues={setOldValues}
                  data={values}
                  handleModalSale={handleModalSale}
                  refreshCards={refreshCards}
                />
              </Grid>
            ))}

          {cardValues && cardValues.length > 0 && filteredCards.length === 0 && !salesSessionsInfo.loading && (
            <Grid
              item
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '30px 15px',
                textAlign: 'center',
                backgroundColor: '#f8fafc',
                borderRadius: 8,
                border: '1px dashed #cbd5e1',
                margin: '8px 0',
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                {filterMode === 'today'
                  ? 'Nenhuma comanda atualizada no dia de hoje.'
                  : 'Nenhuma comanda encontrada para a busca.'}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => {
                  setFilterMode('all');
                  setSearchTerm('');
                }}
              >
                Ver todas as comandas
              </Button>
            </Grid>
          )}
        </Grid>
      </UIContainer>

      {modalValues && (
        <Dialog
          fullScreen={mobile}
          fullWidth
          maxWidth="md"
          open={modalSale}
          onClose={handleModalSale}
          PaperProps={{
            style: mobile
              ? { height: '100%', maxHeight: '100dvh', margin: 0, overflow: 'hidden' }
              : {},
          }}
        >
          <SaleNew
            saleMetadata={modalValues.saleMetadata}
            refreshCards={refreshCards}
            handleModalSale={handleModalSale}
          />
        </Dialog>
      )}

      <ClientCreateModal
        open={openClientModal}
        onClose={() => {
          setOpenClientModal(false);
          setAutocompleteKey((k) => k + 1);
        }}
        initialName={customName}
        onSuccess={(createdCustomer) => {
          setOpenClientModal(false);
          setAutocompleteKey((k) => k + 1);
          customersLoad();
          onSubmitNewSession(null, createdCustomer);
        }}
      />
    </>
  );
};

export default NewSaleSession;